import React, { useCallback, useRef } from 'react';
import SidePanel from 'features/side-panels/components/SidePanel';
import { useSidePanel } from 'features/side-panels/hooks/useSidePanel';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectToolbarPlacement } from 'features/toolbar/toolbarSlice';
import {
  selectPinnedSplitRatio,
  selectIsSidePanelOpen,
  pinnedSplitRatioChanged,
} from 'features/side-panels/sidePanelsSlice';
import { Box, Portal } from '@mui/material';
import { SidePanelType } from 'features/side-panels/types';
import { ResizeDivider } from 'features/side-panels/components/ResizeDivider';
import { PanelSlot } from 'features/side-panels/components/PanelSlot';
import { PanelEntry, useLazyPanels } from 'features/side-panels/hooks/useLazyPanels';

const SidePanelManager = () => {
  const { sidePanel, pinnedPanels, closePanel } = useSidePanel();
  const dispatch = useAppDispatch();

  const { panels, onError, onLoaded, isExhausted } = useLazyPanels({
    activePanel: sidePanel,
    pinnedPanels,
  });

  const toolbarPlacement = useAppSelector(selectToolbarPlacement);
  const isOpen = useAppSelector(selectIsSidePanelOpen);
  const splitRatio = useAppSelector(selectPinnedSplitRatio);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleRatioChange = useCallback((ratio: number) => {
    containerRef.current?.style.setProperty('--panels-split-ratio', String(ratio));
  }, []);

  const handleDragEnd = useCallback(
    (ratio: number) => {
      dispatch(pinnedSplitRatioChanged(ratio));
    },
    [dispatch]
  );

  return (
    <Portal>
      <SidePanel
        open={isOpen}
        onBackdropClick={() => {
          if (sidePanel) {
            closePanel(sidePanel);
          }
        }}
        placement={toolbarPlacement}
      >
        <Box
          ref={containerRef}
          style={{ '--panels-split-ratio': splitRatio } as React.CSSProperties}
          sx={{ height: '100%', position: 'relative' }}
        >
          {(Object.entries(panels) as [SidePanelType, PanelEntry][]).map(
            ([type, { Panel, id }]) => (
              <PanelSlot
                key={id}
                type={type}
                Panel={Panel}
                pinnedPanels={pinnedPanels}
                activePanel={sidePanel}
                exhausted={isExhausted(type)}
                onError={onError}
                onLoaded={onLoaded}
              />
            )
          )}
          {pinnedPanels.length === 2 && !sidePanel && (
            <ResizeDivider onRatioChange={handleRatioChange} onDragEnd={handleDragEnd} />
          )}
        </Box>
      </SidePanel>
    </Portal>
  );
};

export default SidePanelManager;
